import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { TravelManagementCompany as TravelManagementCompanies } from '@mynt/backoffice-api-spec/travel-card'
import useMaterialNotification from 'hooks/useMaterialNotification.tsx'
import { useState } from 'react'
import { BorderContainer, BorderContainerHeader } from '../../../helpers/CreditOnCardHelpers'
import { useExternalCustomer, useRegisterExternalCustomer } from '../api/useTravelCard'
import type { ExternalCustomer, TravelManagementCompany } from '../types'

const travelManagementCompanies = Object.values(TravelManagementCompanies)

type ExternalCustomerSectionProps = {
  customerId: string
}

export const ExternalCustomerSection = ({ customerId }: ExternalCustomerSectionProps) => {
  const { data: externalCustomer, isLoading, isError } = useExternalCustomer(customerId)

  return (
    <BorderContainer fullWidth gap="16px">
      <BorderContainerHeader style={{ marginBottom: 0 }}>
        <Typography variant="h3">Travel Management Company (TMC)</Typography>
      </BorderContainerHeader>
      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">Could not load TMC account.</Alert>}
      {!isLoading && !isError && (
        <>
          {!externalCustomer && <Alert severity="info">Customer is not registered at a TMC.</Alert>}
          <ExternalCustomerForm customerId={customerId} externalCustomer={externalCustomer ?? undefined} />
        </>
      )}
    </BorderContainer>
  )
}

type ExternalCustomerFormProps = {
  customerId: string
  externalCustomer?: ExternalCustomer
}

const ExternalCustomerForm = ({ customerId, externalCustomer }: ExternalCustomerFormProps) => {
  const notify = useMaterialNotification()
  const registerExternalCustomer = useRegisterExternalCustomer(customerId)
  const [travelManagementCompany, setTravelManagementCompany] = useState<TravelManagementCompany | ''>(
    externalCustomer?.travelManagementCompany ?? '',
  )
  const [externalId, setExternalId] = useState(externalCustomer?.tmcId ?? '')

  const save = (company: TravelManagementCompany, tmcId: string) =>
    registerExternalCustomer.mutate({ travelManagementCompany: company, tmcId }, { onSuccess: () => notify('TMC account saved') })

  const handleCompanyChange = (company: TravelManagementCompany) => {
    setTravelManagementCompany(company)
    const id = externalId.trim()
    if (id) save(company, id)
  }

  const handleExternalIdBlur = () => {
    const id = externalId.trim()
    if (!id || !travelManagementCompany || id === externalCustomer?.tmcId) return
    save(travelManagementCompany, id)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        label="TMC"
        value={travelManagementCompany}
        onChange={(e) => handleCompanyChange(e.target.value as TravelManagementCompany)}
        fullWidth
      >
        {travelManagementCompanies.map((company) => (
          <MenuItem key={company} value={company}>
            {company}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="TMC id"
        value={externalId}
        onChange={(e) => setExternalId(e.target.value)}
        onBlur={handleExternalIdBlur}
        fullWidth
      />
    </Box>
  )
}
